import '../ComponentNotificationConfig';
import { useReactiveVar } from '@apollo/client';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { GridScrollStyled, MIconButtonStyle } from './NotificationComponentStyles';
import { emptyNotifications, renderNotification } from '../../../cache/learner/homeReactiveVars';
import AtomBadge from '../../../components/atoms/AtomBadge/AtomBadge';
import AtomDivider from '../../../components/atoms/AtomDivider';
import AtomFlex from '../../../components/atoms/AtomFlex';
import AtomGrid from '../../../components/atoms/AtomGrid';
import AtomTypography from '../../../components/atoms/AtomTypography/AtomTypography';
import EmptyStateComponent from '../../../components/generics/EmptyStateComponent/EmptyStateComponent';
import BellNotificationIcon from '../../../components/icons/svg/BellNotificactionIcon';
import CheckDoubleIcon from '../../../components/icons/svg/CheckDoubleIcon';
import GraduateOwlIcon from '../../../components/icons/svg/GraduateOwlIcon';
import { styleConfigs } from '../../../components/resources/styleConfigs';
import MenuPopover from '../../../components/template-minimal/MenuPopoverComponent/MenuPopover';
import { TestIdContext, getTestAttrs } from '../../../constants/testingAttributes';
import userAuth from '../../../hooks/userAuth';
import { Mixpanel } from '../../../mixpanel/mixpanel';
import { MixpanelComponentsNames } from '../../../mixpanel/MixpanelEvents/mixpanelComponentNames';
import { MixpanelEventsEnum } from '../../../mixpanel/MixpanelEvents/mixpanelEventsInterface';
import { useMutationDeleteNotifications } from '../../../operations/mutations/student/useDeleteStudentNotification/useDeleteStudentNotification';
import palette from '../../../theme/learnerPalette';
import { getMixpanelName } from '../../../utils/getMixpanelName';
import { ROLE_ENUM } from '../../../views/Auth/Login/interfaces/IGoogleUser';
import CardNotificationComponent from '../CardNotificationComponent/CardNotificationComponent';
import MobileListComponent from '../MobileListComponent/MobileListComponent';

const NotificationComponent: React.FC = () => {
  const { auth } = userAuth();
  const theme = useTheme();
  const { t } = useTranslation('ComponentNotification');
  const anchorRef = useRef(null);
  const [open, setOpen] = useState<boolean>(false);
  const [fromKeyboard, setFromKeyboard] = useState(0);
  const [deleteNotifications] = useMutationDeleteNotifications();
  const allNotifications = useReactiveVar(renderNotification);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { pathname, search } = useLocation();
  const isLearner: boolean = auth?.userRole === ROLE_ENUM.STUDENT;

  const handleChangeOpen = async (): Promise<void> => {
    setFromKeyboard(0);
    setOpen(!open);
    const updateNotificationWhenClose = !!open;
    const notificationsIdsToUpdate = allNotifications.newNotifications.map(({ id }) => id);

    if (updateNotificationWhenClose && !!notificationsIdsToUpdate.length) {
      renderNotification(emptyNotifications);
      await deleteNotifications({
        variables: {
          studentId: auth?.student?.id ?? '',
        },
      });
      Mixpanel.track(
        {
          action: MixpanelEventsEnum.CLEAR_NOTIFICATIONS,
          data: {
            screen: getMixpanelName(pathname.concat(search)),
            component: MixpanelComponentsNames.NOTIFICATION_COMPONENT,
            action: 'click',
          },
        },
        isLearner,
      );
    } else {
      Mixpanel.track(
        {
          action: MixpanelEventsEnum.VIEW_NOTIFICATIONS,
          data: {
            screen: getMixpanelName(pathname.concat(search)),
            component: MixpanelComponentsNames.NOTIFICATION_COMPONENT,
            action: 'click',
          },
        },
        isLearner,
      );
    }
  };

  const handleKeyDownButton = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    const keyPressed = e.key;

    switch (keyPressed) {
      case 'ArrowRight':
        setFromKeyboard(1);
        break;

      case 'ArrowLeft':
        setFromKeyboard(1);
        break;

      case 'End':
        setFromKeyboard(1);
        break;

      case 'Home':
        setFromKeyboard(1);
        break;

      case 'Tab':
        setFromKeyboard(1);
        break;

      default:
        setFromKeyboard(0);

        break;
    }
  };

  return (
    <>
      <MIconButtonStyle
        {...getTestAttrs(TestIdContext.NOTIFICATION, 'button', {
          count: allNotifications.newNotifications.length,
          isOpen: String(open),
        })}
        aria-label={t('NOTIFICATION_BUTTON_ARIA_LABEL')}
        ref={anchorRef}
        theme={theme}
        fromkeyboard={fromKeyboard}
        onClick={handleChangeOpen}
        onKeyDown={handleKeyDownButton}
      >
        <AtomBadge
          {...getTestAttrs(TestIdContext.NOTIFICATION, 'notification_badge')}
          componentsProps={{
            badge: {
              'aria-label': t('NOTIFICATION_BADGE_UNREAD', { count: allNotifications.newNotifications.length }),
            },
          }}
          badgeContent={allNotifications.newNotifications.length}
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontWeight: styleConfigs.fonts.weight500,
              fontSize: styleConfigs.fonts.fontSize12,
              color: theme.palette.text.white,
              background: theme.palette.background.firebrick,
            },
          }}
        >
          <BellNotificationIcon aria-hidden="true" />
        </AtomBadge>
      </MIconButtonStyle>
      {isSmallScreen ? (
        <MobileListComponent open={open} onClose={handleChangeOpen} data={allNotifications} />
      ) : (
        <MenuPopover
          open={open}
          onClose={handleChangeOpen}
          anchorEl={anchorRef.current}
          sx={{ borderRadius: `${styleConfigs.borders.border16}px` }}
          width={styleConfigs.dimensions.width360}
        >
          <AtomGrid
            container
            direction="row"
            alignItems="center"
            padding={`${styleConfigs.marginPadding.custom20}px 
            ${styleConfigs.marginPadding.custom16}px`}
          >
            <AtomGrid item sm={10}>
              <AtomTypography variant="h6">{t('NOTIFICATION_TEXT_TITLE')}</AtomTypography>
              <AtomTypography variant="subtitle2" color={palette.light.text.mistBlue}>
                {t('NOTIFICATION_TEXT_UNREAD_NOTIFICATION', {
                  unread: allNotifications.newNotifications.length.toString(),
                })}
              </AtomTypography>
            </AtomGrid>
            <AtomGrid item sm={2} justifyContent="center" alignItems="flex-start">
              <AtomFlex direction="column" alignItems="flex-end">
                <CheckDoubleIcon
                  width={styleConfigs.dimensions.width26}
                  height={styleConfigs.dimensions.height26}
                  color={palette.light.icons.prussianBlue}
                />
              </AtomFlex>
            </AtomGrid>
          </AtomGrid>
          <AtomDivider />
          {!allNotifications.newNotifications.length && !allNotifications.beforeNotifications.length ? (
            <AtomGrid height={styleConfigs.dimensions.height560}>
              <EmptyStateComponent
                title={t('NOTIFICATION_EMPTY_STATE_TITLE')}
                fontSizeTitle={styleConfigs.fonts.fontSize18}
                gapIconTitle={styleConfigs.marginPadding.empty}
                subtitle={t('NOTIFICATION_EMPTY_STATE_SUBTITLE')}
                fontSizeSubtitle={styleConfigs.fonts.fontSize14}
                svgIconComponent={
                  <GraduateOwlIcon width={styleConfigs.dimensions.width80} height={styleConfigs.dimensions.height80} />
                }
              />
            </AtomGrid>
          ) : (
            <GridScrollStyled>
              {allNotifications.newNotifications.map((notification) => (
                <CardNotificationComponent key={notification.id.toString()} notificationDetails={notification} />
              ))}
              {allNotifications.beforeNotifications.map((notification) => (
                <CardNotificationComponent
                  key={notification.id.toString()}
                  notificationDetails={notification}
                  beforeNotification
                />
              ))}
            </GridScrollStyled>
          )}
        </MenuPopover>
      )}
    </>
  );
};

export default NotificationComponent;
